import { Injectable, computed, inject, signal } from "@angular/core";
import { ANONYMOUS_UUID_HEADER, CLIENT_DOMAIN_HEADER, CUSTOMER_UUID_HEADER, ChatbotDomainRules, Domain, domainRules } from "../models/domain";
import { CHATBOT_CONFIG } from "../config";
import { v7 as uuidv7 } from "uuid";

/**
 * Browser storage keys.
 *
 * These strings are deliberately frozen. They are not part of any wire contract, and renaming one
 * would orphan the value an existing visitor already has, handing them a brand new identity and a
 * brand new conversation history on their next visit. `clientToken` and `userId` predate this
 * service and keep their original names for the same reason.
 */
const STORAGE_KEYS = {
    clientToken: "clientToken",
    userId: "userId",
    anonymousUuid: "anonymousId",
    customerUuid: "customerId",
} as const;

/** A domain the host declared, resolved once so the rules cannot drift from the name. */
interface DeclaredDomain {
    name: Domain;
    rules: ChatbotDomainRules;
}

/**
 * Owns *who* the current user is and therefore how every request identifies itself.
 *
 * Three responsibilities that are deliberately kept together because they are the same
 * decision seen from different angles:
 *
 * 1. **Domain**: declared by the host, once, out of the domains its configuration lists. A
 *    tester may put a second one on top of it for the length of a test conversation.
 * 2. **Identity header**: exactly one per request, derived from the domain's rules. The domain
 *    without a credential is the only one that switches header at runtime, from the anonymous one
 *    to the customer one once the visitor converts into a lead.
 */
@Injectable({ providedIn: "root" })
export class IdentityService {
    private readonly config = inject(CHATBOT_CONFIG);

    /**
     * The domain the host declared, and null until it has.
     *
     * A host shares its HttpClient with this library, so its own calls to the chatbot API can run
     * before the widget has ever been mounted. Until the domain is declared no identity is
     * asserted on those calls at all. Without this, a host on a domain with a credential would
     * send an anonymous identity instead, and would mint and persist an anonymous UUID in that
     * user's browser for a visitor that does not exist.
     */
    private readonly _host = signal<DeclaredDomain | null>(null);

    /**
     * A second domain, signed into on purpose, that replaces the host's own for as long as a test
     * conversation lasts.
     *
     * Kept separate from `_host` rather than overwriting it: the host's own credential is still
     * needed, both to authorise the test and to go back to normal without asking the host for its
     * credentials again. Never persisted. A foreign domain's credential lives for this tab and
     * this conversation only, and a reload asks for a fresh sign in.
     */
    private readonly _test = signal<DeclaredDomain | null>(null);
    private readonly _testToken = signal<string | null>(null);

    /**
     * The visitor this conversation is being held as, while testing a domain that has no
     * credential at all.
     *
     * Minted fresh every time that identity is taken on, and never written to storage. Fresh
     * because a visitor nobody has seen before is the only way to try out what happens to one:
     * reusing an identity would carry over whatever quota or lead state it had already reached.
     * Never persisted because this is the browser of an authenticated user, and an anonymous
     * identity left behind there would outlive the test and be sent on its own next time.
     */
    private readonly _testAnonymousUuid = signal<string | null>(null);

    private readonly _token = signal<string | null>(null);
    private readonly _customerUuid = signal<string | null>(null);

    private readonly _active = computed(() => this._test() ?? this._host());

    /** The domain requests identify as, which is the test one while a test identity is held. */
    readonly domain = computed(() => this._active()?.name ?? null);
    readonly rules = computed(() => this._active()?.rules ?? null);

    /**
     * The domain the host mounted the widget for, whatever identity is being used right now.
     *
     * Every decision about what the widget *offers* reads this one, not `domain`: signing in as
     * another domain's user changes who the backend thinks is asking, not which controls the
     * tester has in front of them.
     */
    readonly hostDomain = computed(() => this._host()?.name ?? null);
    readonly hostRules = computed(() => this._host()?.rules ?? null);

    /** The domain currently being impersonated for a test, or null in normal use. */
    readonly testDomain = computed(() => this._test()?.name ?? null);

    readonly customerUuid = this._customerUuid.asReadonly();

    /** True once the anonymous visitor has been converted into a lead. */
    readonly isRegistered = computed(() => this._customerUuid() !== null);

    /**
     * A domain with a credential cannot query until it has one, since the backend rejects the
     * request without a matching bearer token.
     */
    readonly awaitingCredentials = computed(() => this.rules()?.identity === "bearer" && !this.activeToken());

    constructor() {
        this._customerUuid.set(this.read(STORAGE_KEYS.customerUuid));
    }

    /**
     * Applies the domain the host declared, and throws if it declared none or one its
     * configuration does not list. There is no fallback on purpose: see `domainRules`.
     */
    setDomain(value: Domain | null | undefined): void {
        const rules = domainRules(this.config.domains, value);
        this._host.set({ name: value as Domain, rules });
    }

    /**
     * The session token handed over by the host, for the domains that need one.
     *
     * The host used to publish an account id alongside it. It is no longer asked for: the
     * backend reads the caller's identity out of this token, so an id from the host would be a
     * value nothing checks. Blank and the stringified absence of a value are normalised to
     * `null`, so a host restoring a half empty session leaves `awaitingCredentials` true and the
     * widget waits visibly instead of sending a request it cannot authenticate.
     */
    setCredentials(token: string | null): void {
        this._token.set(this.normalise(token));
    }

    /**
     * Holds a conversation as a user of another declared domain.
     *
     * A domain with a credential needs one, and it has to be that domain's own: each of them is
     * verified under a key of its own, which is exactly what makes signing in worth doing rather
     * than relabelling the credential already in hand. A domain with no credential needs
     * nothing, because there is nothing to hold: its identity is a UUID the visitor mints, and
     * one is minted here.
     */
    startTestIdentity(domain: Domain, token?: string | null): void {
        const rules = domainRules(this.config.domains, domain);

        if (rules.identity === "anonymous") {
            this._test.set({ name: domain, rules });
            this._testToken.set(null);
            this._testAnonymousUuid.set(uuidv7());
            return;
        }

        const normalised = this.normalise(token ?? null);
        if (!normalised) {
            throw new Error(`Chatbot: no credential was returned for domain "${domain}", so the test identity was not taken on.`);
        }
        this._test.set({ name: domain, rules });
        this._testToken.set(normalised);
        this._testAnonymousUuid.set(null);
    }

    /** Back to the host's own identity and credential, which were never discarded. */
    clearTestIdentity(): void {
        this._test.set(null);
        this._testToken.set(null);
        this._testAnonymousUuid.set(null);
    }

    get clientToken(): string | null {
        return this.read(STORAGE_KEYS.clientToken);
    }

    get userId(): string | null {
        return this.read(STORAGE_KEYS.userId);
    }

    setSession(clientToken: string | null, userId: string | null): void {
        if (clientToken) this.write(STORAGE_KEYS.clientToken, clientToken);
        if (userId) this.write(STORAGE_KEYS.userId, userId);
    }

    /** Drops the current conversation without touching who the user is. */
    clearSession(): void {
        this.remove(STORAGE_KEYS.clientToken);
    }

    /**
     * Every header this request identifies itself with.
     *
     * Always the domain, because the backend derives everything from it. Plus, for a domain
     * without a credential, the visitor's own UUID, minted here on first use and persisted so the
     * same identity survives a reload. That persistence is what makes the free query limit
     * meaningful in the first place. A domain with a credential adds nothing: its bearer token
     * already says who it is.
     */
    identityHeaders(): Record<string, string> {
        const active = this._active();
        if (!active) {
            return {};
        }

        const headers: Record<string, string> = { [CLIENT_DOMAIN_HEADER]: active.name };

        if (active.rules.identity === "anonymous") {
            // A visitor minted for a test is that conversation's whole identity: it has converted
            // into nothing and is deliberately not the one in storage, which belongs to whoever
            // is really using this browser.
            const testUuid = this._testAnonymousUuid();
            if (testUuid) {
                headers[ANONYMOUS_UUID_HEADER] = testUuid;
            } else {
                const customerUuid = this._customerUuid();
                if (customerUuid) {
                    headers[CUSTOMER_UUID_HEADER] = customerUuid;
                } else {
                    headers[ANONYMOUS_UUID_HEADER] = this.anonymousUuid();
                }
            }
        }

        return headers;
    }

    bearerToken(): string | null {
        const active = this._active();
        if (!active || active.rules.identity !== "bearer") {
            return null;
        }
        return this.activeToken();
    }

    /**
     * The host's own credential, while a test identity is held.
     *
     * It travels beside the bearer token, in a header of its own, and answers a different
     * question: not who the agent is answering, but who asked for a test. The backend lets an
     * undeployed configuration run only for a caller that can prove it may ask for one, and the
     * test identity's own credential proves nothing of the sort.
     */
    testAuthorizationToken(): string | null {
        return this._test() ? this._token() : null;
    }

    /**
     * Applies the lead state the backend mirrors out of the anonymous agent's session.
     *
     * A customer uuid arriving is the conversion signal: from the next request on, the client
     * identifies as a customer instead of an anonymous visitor, which is exactly what the
     * backend has already recorded on the session row. Sending a demo request does not produce
     * one, because no customer exists until an operator converts that request.
     *
     * How many free questions are left is deliberately not mirrored here. The backend owns that
     * count and acts on it by steering the conversation, so a copy kept on this side could only
     * disagree with it, and anything built on the copy would be gating a conversation the agent
     * intends to continue.
     */
    applyLeadState(state: { customerUuid?: string | null }): void {
        if (state.customerUuid) {
            this.convertToCustomer(state.customerUuid);
        }
    }

    /** Whichever credential the current identity travels with. */
    private activeToken(): string | null {
        return this._test() ? this._testToken() : this._token();
    }

    /**
     * Promotes the anonymous visitor to a registered customer: the anonymous identity is dropped,
     * so `identityHeaders()` starts sending the customer header.
     */
    private convertToCustomer(customerUuid: string): void {
        this._customerUuid.set(customerUuid);
        this.write(STORAGE_KEYS.customerUuid, customerUuid);
        this.remove(STORAGE_KEYS.anonymousUuid);
    }

    /**
     * The visitor's own identifier, minted on first use and kept in browser storage.
     *
     * Version 7 rather than version 4, matching every other identifier this ecosystem persists:
     * the value sorts chronologically, which keeps index locality on the column it lands in, and
     * it carries the moment it was created, which makes an identity traceable in an audit without
     * a side table.
     */
    private anonymousUuid(): string {
        let uuid = this.read(STORAGE_KEYS.anonymousUuid);
        if (!uuid) {
            uuid = uuidv7();
            this.write(STORAGE_KEYS.anonymousUuid, uuid);
        }
        return uuid;
    }

    /** Treats blank and the stringified absence of a value as no value at all. */
    private normalise(value: string | null): string | null {
        if (value === null || value === undefined) {
            return null;
        }
        const trimmed = value.trim();
        return trimmed === "" || trimmed === "undefined" || trimmed === "null" ? null : trimmed;
    }

    /**
     * Browser storage is unavailable in some embedded web views and when third party storage is
     * blocked, both of which apply to an iframe host. Failing to persist must degrade the limit,
     * never break the chat.
     */
    private read(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private write(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            /* storage unavailable: state stays in memory for this session only */
        }
    }

    private remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch {
            /* storage unavailable */
        }
    }
}
